import type { StepSpec } from '@dru/ui';

/**
 * A medical case's journey, as the person looking at it needs to read it.
 *
 * The examination state machine has fifteen states; the reader wants three
 * answers — where is this case now, what happens next, and who has to act.
 * This maps the one onto the other. It reads only the status and the
 * operational facts every console already receives (a lab, an appointment),
 * never a result or a decision, so the same journey can be drawn for
 * Operations, which is shown nothing clinical, and for HR.
 *
 * The API stays the authority on what may happen next. This decides only how
 * the current state is described.
 */

export type JourneyActor = 'Company HR' | 'Operations' | 'Employee' | 'Venue desk' | 'Laboratory' | 'Physician';

export interface CaseJourney {
  steps: StepSpec[];
  /** Where the case is, in a few words. */
  now: string;
  /** The next thing that has to happen, or null once the case is closed. */
  next: string | null;
  /** Who does it. Null when nobody has to — certified, cancelled. */
  actor: JourneyActor | null;
  /** Set when the case left the journey: cancelled, expired, a closed no-show. */
  halted: string | null;
}

export interface JourneyInput {
  status: string;
  lab?: string | null;
  appointmentAt?: string | null;
  /** Formats the appointment for the step's detail line. */
  formatWhen?: (iso: string) => string;
}

const STEPS = [
  { key: 'created', label: 'Case created' },
  { key: 'lab', label: 'Lab assigned' },
  { key: 'appointment', label: 'Appointment' },
  { key: 'testing', label: 'Examination' },
  { key: 'verified', label: 'Lab verified' },
  { key: 'review', label: 'Physician review' },
  { key: 'done', label: 'Certified' },
] as const;

type StepKey = typeof STEPS[number]['key'];

/**
 * Per status: the step the case is on, whether it is stuck there, and the
 * sentence for each of now / next / who.
 */
const AT: Record<string, { step: StepKey | 'end'; blocked?: string; now: string; next: string | null; actor: JourneyActor | null }> = {
  // Not yet raised: the case is still being created, so "created" is the
  // current step rather than a finished one.
  DRAFT: { step: 'created', now: 'Draft — not yet raised', next: 'Raise the case', actor: 'Company HR' },
  REQUESTED: { step: 'lab', now: 'Waiting for a lab', next: 'Assign a lab and branch', actor: 'Operations' },
  ROUTED: { step: 'appointment', now: 'Lab assigned', next: 'Book the appointment', actor: 'Operations' },
  CONSENT_PENDING: { step: 'appointment', now: 'Waiting for the employee’s consent', next: 'Employee gives consent', actor: 'Employee' },
  SCHEDULED: { step: 'testing', now: 'Awaiting the visit', next: 'Check the employee in on the day', actor: 'Venue desk' },
  NO_SHOW_OPEN: { step: 'appointment', blocked: 'Missed — needs rebooking', now: 'Employee did not attend', next: 'Rebook the appointment', actor: 'Operations' },
  CHECKED_IN: { step: 'testing', now: 'Checked in at the venue', next: 'Complete the stations and collect samples', actor: 'Venue desk' },
  IN_PROGRESS: { step: 'testing', now: 'Examination under way', next: 'Complete the stations and collect samples', actor: 'Venue desk' },
  LAB_PENDING: { step: 'verified', now: 'Samples at the laboratory', next: 'Enter, validate and release the results', actor: 'Laboratory' },
  REVIEW_PENDING: { step: 'review', now: 'Waiting for the physician', next: 'Review the results and sign a fitness decision', actor: 'Physician' },
  RETEST_REQUIRED: { step: 'testing', blocked: 'Retest requested', now: 'A test has to be repeated', next: 'Book the retest', actor: 'Operations' },
  CERTIFIED: { step: 'end', now: 'Certified', next: null, actor: null },
  NO_SHOW_CLOSED: { step: 'appointment', now: 'Closed after a missed appointment', next: null, actor: null },
  EXPIRED: { step: 'lab', now: 'Expired', next: null, actor: null },
  CANCELLED: { step: 'lab', now: 'Cancelled', next: null, actor: null },
};

const HALTED: Record<string, string> = {
  CANCELLED: 'Cancelled — this case will not continue.',
  EXPIRED: 'Expired before it was completed.',
  NO_SHOW_CLOSED: 'Closed after the employee missed the appointment.',
};

export function caseJourney(c: JourneyInput): CaseJourney {
  const at = AT[c.status] ?? { step: 'lab' as const, now: c.status.replace(/_/g, ' ').toLowerCase(), next: null, actor: null };
  const halted = HALTED[c.status] ?? null;
  const current = at.step === 'end' ? STEPS.length : STEPS.findIndex((s) => s.key === at.step);
  const when = c.appointmentAt ? (c.formatWhen ? c.formatWhen(c.appointmentAt) : c.appointmentAt) : null;

  const steps: StepSpec[] = STEPS.map((s, i) => {
    const state: StepSpec['state'] = i < current ? 'done'
      : i === current && !halted ? (at.blocked ? 'blocked' : 'current')
        : 'upcoming';
    let detail: StepSpec['detail'];
    if (i === current && at.blocked) detail = at.blocked;
    // The lab is named once the case has passed the step. On a case still
    // waiting to be routed a lab name read as "assigned" when it was only
    // proposed.
    else if (s.key === 'lab' && c.lab && state === 'done') detail = c.lab;
    else if (s.key === 'appointment' && when && (state === 'done' || state === 'current')) detail = when;
    return { key: s.key, label: s.label, state, ...(detail ? { detail } : {}) };
  });

  return { steps, now: at.now, next: halted ? null : at.next, actor: halted ? null : at.actor, halted };
}
